import React, { useState } from "react";
import {
  DMModal,
  DMModalContent,
  DMModalFooter,
  DMModalTitle,
} from "../../../shared/DMModal";
import Button from "../../../student/components/generic/button";
import { SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { useUpdateRenewal } from "./useUpdateRenewal";
import { MailListEntry, RenewalTableItem } from "../../utils/quoteUtils";
import { CopyContactAttrToClipboard } from "./CopyContactAttrToClipboard";

type FormValues = {
  contacts: MailListEntry[];
  newContacts: MailListEntry[];
};

export const EditContactsModal: React.FC<{
  renewal: RenewalTableItem;
  visible: boolean;
  onClose: () => void;
}> = ({ renewal, visible, onClose }) => {
  const { _id: renewalId, contacts = [] } = renewal;

  const updateRenewal = useUpdateRenewal();
  const [isSaving, setIsSaving] = useState(false);

  const {
    register,
    control,
    formState: { dirtyFields, errors },
    handleSubmit,
    getValues,
  } = useForm<FormValues>({
    defaultValues: { contacts, newContacts: [] },
  });

  const { fields: existingContactsFields, remove: removeExistingContact } =
    useFieldArray({
      name: "contacts",
      control,
    });

  const {
    fields: newContactsFields,
    append: appendNewContact,
    remove: removeNewContact,
  } = useFieldArray({
    name: "newContacts",
    control,
  });

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setIsSaving(true);
    try {
      await updateRenewal(renewalId, {
        contacts: [...data.contacts, ...data.newContacts],
      });
      setIsSaving(false);
      onClose();
    } catch (error) {
      setIsSaving(false);
    }
  };

  return (
    <DMModal visible={visible} onClose={onClose}>
      <form
        className="flex w-full flex-col gap-10"
        onSubmit={handleSubmit(onSubmit)}
      >
        <DMModalTitle>Contacts and Links</DMModalTitle>
        <DMModalContent>
          <p className="w-full -translate-y-8 text-center font-bold">
            Editing contacts for {renewal.name}
          </p>
          {renewal.disabled && (
            <div className="mb-2 w-full rounded border border-dm-warning-500 bg-dm-warning-100 px-2 py-1 text-sm">
              <strong>This renewal is currently disabled.</strong> It is not
              possible to copy links for disabled renewals.
            </div>
          )}
          <ul className="flex w-full flex-col gap-1">
            {existingContactsFields.length ? (
              existingContactsFields.map((field, index) => {
                const link = getValues(`contacts.${index}.link`);
                return (
                  <li key={field.id} className="flex w-full flex-col gap-2">
                    <span className="flex w-full gap-2">
                      <input
                        className="h-9 w-full rounded border border-dm-charcoal-200 px-2 py-0"
                        type="email"
                        {...register(`contacts.${index}.email`, {
                          required: true,
                        })}
                      />
                      <CopyContactAttrToClipboard
                        getAttr={() => link}
                        title={
                          renewal.disabled
                            ? "Copying links is disabled for this renewal"
                            : link
                            ? "Copy link to clipboard"
                            : "No link for this contact"
                        }
                        icon={<i className="far fa-link" />}
                        disabled={
                          !link || dirtyFields.contacts?.[index]?.email === true
                        }
                      />
                      <CopyContactAttrToClipboard
                        getAttr={() => getValues(`contacts.${index}.link`)}
                        title="Copy email to clipboard"
                        icon={<i className="far fa-clipboard" />}
                      />
                      <button
                        className="w-6 text-center text-3xl text-dm-error-500 hover:text-dm-error-600"
                        type="button"
                        title="Remove this contact"
                        onClick={() => removeExistingContact(index)}
                      >
                        <span className="block -translate-y-[2px]">
                          &times;
                        </span>
                      </button>
                    </span>
                  </li>
                );
              })
            ) : (
              <i className="text-dm-gray-500">No contacts for this customer</i>
            )}
          </ul>

          {newContactsFields.length > 0 && (
            <ul className="flex w-full flex-col gap-2">
              {newContactsFields.map((field, index) => (
                <li
                  className="relative flex w-full flex-col gap-3 rounded bg-dm-background-blue-100 p-4"
                  key={field.id}
                >
                  <button
                    className="group absolute right-1 top-0 px-1 text-xl"
                    type="button"
                    onClick={() => removeNewContact(index)}
                  >
                    <i className="far fa-times !text-black opacity-50 group-hover:opacity-100" />
                  </button>
                  <div className="flex w-full flex-col gap-1">
                    <label
                      className="text-xs font-bold"
                      htmlFor={`newContacts-${index}-email`}
                    >
                      Email
                    </label>
                    <input
                      className="h-9 w-full rounded border border-dm-charcoal-200 px-2 py-0"
                      id={`newContacts-${index}-email`}
                      type="email"
                      {...register(`newContacts.${index}.email`, {
                        required: "This field is required",
                      })}
                    />
                    {errors?.newContacts?.[index]?.email && (
                      <span className="text-xs text-dm-error-500">
                        {errors.newContacts[index]?.email?.message ||
                          "Unknown error"}
                      </span>
                    )}
                  </div>
                  <div className="flex w-full gap-3">
                    <div className="flex w-full flex-col gap-1">
                      <label
                        className="text-xs font-bold"
                        htmlFor={`newContacts-${index}-firstName`}
                      >
                        First name
                      </label>
                      <input
                        className="h-9 w-full rounded border border-dm-charcoal-200 px-2 py-0"
                        id={`newContacts-${index}-firstName`}
                        {...register(`newContacts.${index}.firstName`, {
                          required: "This field is required",
                        })}
                      />
                      {errors?.newContacts?.[index]?.firstName && (
                        <span className="text-xs text-dm-error-500">
                          {errors.newContacts[index]?.firstName?.message ||
                            "Unknown error"}
                        </span>
                      )}{" "}
                    </div>
                    <div className="flex w-full flex-col gap-1">
                      <label
                        className="text-xs font-bold"
                        htmlFor={`newContacts-${index}-lastName`}
                      >
                        Last name
                      </label>
                      <input
                        className="h-9 w-full rounded border border-dm-charcoal-200 px-2 py-0"
                        id={`newContacts-${index}-lastName`}
                        {...register(`newContacts.${index}.lastName`, {
                          required: "This field is required",
                        })}
                      />
                      {errors?.newContacts?.[index]?.lastName && (
                        <span className="text-xs text-dm-error-500">
                          {errors.newContacts[index]?.lastName?.message ||
                            "Unknown error"}
                        </span>
                      )}{" "}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <Button
            className="!py-0"
            type="link"
            submit={false}
            onClick={() => appendNewContact({ email: "", link: "" })}
          >
            + Add contact
          </Button>
        </DMModalContent>
        <DMModalFooter>
          <Button onClick={onClose} type="outline" submit={false}>
            Cancel
          </Button>
          <Button isLoading={isSaving}>Save contacts</Button>
        </DMModalFooter>
      </form>
    </DMModal>
  );
};
